import api from './api'
import type { InvoiceRecord, InvoiceSummary } from '@/types'

export const invoicesService = {
  async list(options?: { skip?: number; take?: number; status?: string; search?: string }) {
    const params = new URLSearchParams()
    if (options?.skip !== undefined) params.set('skip', String(options.skip))
    if (options?.take !== undefined) params.set('take', String(options.take))
    if (options?.status) params.set('status', options.status)
    if (options?.search) params.set('search', options.search)
    const { data } = await api.get(`/invoices?${params}`)
    return data.data as { invoices: InvoiceRecord[]; total: number }
  },

  async getById(id: string) {
    const { data } = await api.get(`/invoices/${id}`)
    return data.data as InvoiceRecord
  },

  async create(params: {
    proposalId: string
    invoiceType?: string
    depositPercent?: number
    paymentTerms?: string
    taxRate?: number
    notes?: string
  }) {
    const { data } = await api.post('/invoices', params)
    return data.data as InvoiceRecord
  },

  async update(invoiceId: string, updates: {
    paymentTerms?: string
    taxRate?: number
    notes?: string
    termsAndConditions?: string
    invoiceType?: string
    depositPercent?: number
  }) {
    const { data } = await api.put(`/invoices/${invoiceId}`, updates)
    return data.data as InvoiceRecord
  },

  async send(invoiceId: string, emails: string[]) {
    const { data } = await api.post(`/invoices/${invoiceId}/send`, { emails })
    return data.data
  },

  async recordPayment(
    invoiceId: string,
    payment: {
      amount: number
      method: string
      reference?: string
      notes?: string
    }
  ) {
    const { data } = await api.post(`/invoices/${invoiceId}/record-payment`, payment)
    return data.data as InvoiceRecord
  },

  async voidInvoice(invoiceId: string, reason: string) {
    const { data } = await api.post(`/invoices/${invoiceId}/void`, { reason })
    return data.data as InvoiceRecord
  },

  async convertToStaged(invoiceId: string, stageCount: 2 | 3, percentages?: number[]) {
    const { data } = await api.post(`/invoices/${invoiceId}/convert-to-staged`, { stageCount, percentages })
    return data.data as InvoiceRecord[]
  },

  async convertToOneTime(proposalId: string) {
    const { data } = await api.post('/invoices/convert-to-one-time', { proposalId })
    return data.data as InvoiceRecord
  },

  async updateStagePercentages(proposalId: string, percentages: { invoiceId: string; percent: number }[]) {
    const { data } = await api.post('/invoices/update-stage-percentages', { proposalId, percentages })
    return data.data as InvoiceRecord[]
  },

  async getByProposal(proposalId: string) {
    const { data } = await api.get(`/invoices/by-proposal/${proposalId}`)
    return data.data as InvoiceRecord[]
  },

  async getSummary() {
    const { data } = await api.get('/invoices/summary')
    return data.data as InvoiceSummary
  },

  async issueNextScheduledStage(proposalId: string) {
    const { data } = await api.post(`/invoices/proposals/${proposalId}/issue-next-stage`, {})
    return data.data as InvoiceRecord
  },

  async downloadPdf(invoiceId: string): Promise<Blob> {
    const { data } = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' })
    return data as Blob
  },

  async getPublic(shareableLink: string) {
    const { data } = await api.get(`/invoices/public/${shareableLink}`)
    return data.data
  },
}
